import {
    Badge,
    Button,
    Dialog,
    DialogContent,
    Tooltip,
} from "@jhool-io/fe-components";
import { parseISO } from "date-fns";
import { useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { IWeeklyScheduleDashboard } from "../../../../../schedule/types/schedule.types";
import {
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    formatDate,
    isDateInCurrentWeek,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../../../utils/helpers";
import RefreshClockIcon from "../../../../../../components/Icons/RefreshClock";
import MultiplyCircleIcon from "../../../../../../components/Icons/MultiplyCircle";
import CancelSessionForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/CancelSessionForm/CancelSessionForm";
import EditSessionTimeForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/EditSessionTimeForm/EditSessionTimeForm";
import moment from "../../../../../../utils/moment";

interface WeekViewItemProps {
    schedule: IWeeklyScheduleDashboard;
}

type Dialogs = "cancel_session" | "reschedule_session";

export default function WeekViewItem({ schedule }: WeekViewItemProps) {
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [searchParams] = useSearchParams();

    const startOfWeekFallback = moment()
        .startOf("isoWeek")
        .clone()
        .startOf("isoWeek")
        .format("YYYY-MM-DD");

    const startOfWeek = searchParams.get("from_date") || startOfWeekFallback;

    const isCurrentWeek = isDateInCurrentWeek(parseISO(startOfWeek));

    const isCancelSessionMutating = useIsMutating(["cancel-session"]);
    const isEditTimeMutating = useIsMutating(["edit-session-time"]);

    const formatTimeToCorrectFormat = (time: number) => {
        if (time < 10) return `0${time}`;
        return time;
    };

    const sessionStartTime = isCurrentWeek
        ? formatDate(schedule.start_date_time || "", false, "hh:mmaa")
        : convertTimeTo12HoursFormat(
              `${formatTimeToCorrectFormat(
                  schedule.session_start_hr
              )}:${formatTimeToCorrectFormat(schedule.session_start_min)}`
          );

    const sessionEndTime = isCurrentWeek
        ? formatDate(schedule.end_date_time || "", false, "hh:mmaa")
        : convertTimeTo12HoursFormat(
              `${formatTimeToCorrectFormat(
                  schedule.session_end_hr
              )}:${formatTimeToCorrectFormat(schedule.session_end_min)}`
          );

    return (
        <>
            <Dialog open={dialogInView === "cancel_session"}>
                <DialogContent
                    title="Cancel session"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => setDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    classNames="pt-24 pb-48"
                    onCancelClick={() => setShouldNavigate(false)}
                    cancelText="Yes,cancel"
                    saveText="No,create No show note"
                    canCancelButtonSubmit
                    cancelBtnFormValue="cancel_client_session"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => setDialogInView(null)}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={schedule.session_history_id || null}
                        action="cancel"
                    />
                </DialogContent>
            </Dialog>
            {schedule.session_history_id &&
            schedule.start_date_time &&
            schedule.end_date_time ? (
                <Dialog open={dialogInView === "reschedule_session"}>
                    <DialogContent
                        title="Edit Session Time"
                        variant="center"
                        handleCloseDialog={() => setDialogInView(null)}
                        isCancelBtnDisabled={isEditTimeMutating > 0}
                        isSubmitBtnDisabled={isEditTimeMutating > 0}
                        showFooter
                        submitBtnFormValue="edit-session-time"
                    >
                        <EditSessionTimeForm
                            sessionHistoryId={schedule.session_history_id}
                            onFormSubmit={() => {
                                setDialogInView(null);
                            }}
                            sessionStartDateTime={schedule.start_date_time}
                            sessionEndDateTime={schedule.end_date_time}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}

            <div className="h-[168px] p-8 rounded-r8  [backdrop-filter:blur(12px)] flex flex-col gap-y-4 relative group bg-[#FEF5E9] border border-[#FBDFBA] text-black">
                <span className="text-xs font-medium leading-[16px]">
                    {displayNameInRightFormat({
                        firstName: schedule.client.first_name,
                        lastName: schedule.client.last_name,
                    })}
                </span>
                <span className="text-text-sub text-[11px] font-medium tracking-[0.22px] uppercase leading-[12px]">
                    {`${sessionStartTime} - ${sessionEndTime}`}
                </span>
                <span className="uppercase text-[var(--text-soft)] text-[11px] font-medium leading-[12px] tracking-[0.22px]">
                    {makeStringFirstLetterCapital(
                        removeEnumUnderscore(schedule.service)
                    )}
                    ..
                </span>
                <span className="uppercase text-[var(--text-soft)] text-[11px] font-medium leading-[12px] tracking-[0.22px]">
                    {schedule.client.insurance_provider_name
                        ? removeEnumUnderscore(
                              makeStringFirstLetterCapital(
                                  schedule.client.insurance_provider_name
                              )
                          )
                        : "SELF PAY"}
                </span>
                <div className="mt-auto ">
                    <div className="flex flex-col gap-y-4 items-start">
                        {schedule.is_paid ? (
                            <Badge
                                content="Paid"
                                bgColor="#BAECD1"
                                textColor="#0E5730"
                            />
                        ) : (
                            <Badge
                                content="Unpaid"
                                bgColor="#FEC1C6"
                                textColor="#711920"
                            />
                        )}
                    </div>
                    <div className="px-[6px] h-[28px] inline-flex items-center gap-x-[18px] rounded-r6 bg-white border border-[rgba(231,231,231,0.91)] shadow-[0px_6px_10px_0px_rgba(14,18,27,0.06),_0px_2px_4px_0px_rgba(14,18,27,0.03)] self-start mt-8">
                        {schedule.session_history_id &&
                        schedule.start_date_time &&
                        schedule.end_date_time ? (
                            <Tooltip content="Reschedule">
                                <Button
                                    size="auto"
                                    variant="normal"
                                    className="p-4 hover:bg-secondary-light"
                                    onClick={() =>
                                        setDialogInView("reschedule_session")
                                    }
                                >
                                    <RefreshClockIcon />
                                </Button>
                            </Tooltip>
                        ) : null}
                        <Tooltip content="Cancel session">
                            <Button
                                size="auto"
                                variant="normal"
                                className="p-4 hover:bg-secondary-light"
                                onClick={() =>
                                    setDialogInView("cancel_session")
                                }
                            >
                                <MultiplyCircleIcon />
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </>
    );
}
